import { Component, OnDestroy, OnInit} from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { filter, first, takeUntil } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { CookieOptions, CookieService } from 'ngx-cookie-service';

import { AuthenticationService } from '../shared/services/authentication.service';
import { NotificationService } from '../notification/notification.service';
import { CommonService } from '../shared/services/common-service.service';
import { environment } from '../../environments/environment';
import { AUTH_PROMPTS } from '../shared/ui-strings';

@Component({ 
  templateUrl: 'login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {

  authPrompts = AUTH_PROMPTS;
  loginForm: FormGroup;
  fetchData: boolean;
  displayManageUser: boolean;
  loading = false;
  submitted = false;
  userName: string;
  returnUrl: string;
  resetPasswordUrl: string = environment.RESET_PASSWORD;
  authenticationSubscription$: Subscription;
  loginLoader: string = 'true';
  private readonly _destroying$ = new Subject<null>();
  loginStatus = false;

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private authenticationService: AuthenticationService,
    private notificationSvc: NotificationService,
    private commonService: CommonService,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private cookieService: CookieService
  ) {};

  ngOnInit(): void {
    if(this.authenticationService.isMsalLoggedIn()) {
      sessionStorage.setItem('isAuthLoading', 'true');
    }
    else {
      if(!sessionStorage.getItem('isAuthLoading')){
        sessionStorage.setItem('isAuthLoading', 'false');
      }
      this.loginLoader = sessionStorage.getItem('isAuthLoading')!;
    }

    // redirect to home if already logged in
    if (this.authenticationService.currentUserValue) {
      this.router.navigate(['/']);
      // SSO Apps Portal handoff: already authenticated via the #session fragment — skip the MSAL
      // flow below, which would otherwise navigate back to /login (this tab has no MSAL account).
      return;
    }

    this.loginForm = this.formBuilder.group({
      username: ['', Validators.required],
      password: ['', Validators.required],
    });

    this.msalService.handleRedirectObservable().subscribe({
      next: (result) => {
        if (!result) {
          this.setSessionStorageAndLoader("false");
        }
      },
      error: () => {
        this.setSessionStorageAndLoader("false");
      }
    });

    this.msalBroadcastService.msalSubject$
    .pipe(
      filter((msg: EventMessage) => msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED),
    )
    .subscribe((result: EventMessage) => {
      if (this.msalService.instance.getAllAccounts().length === 0) {
        this.router.navigate(['/login']);
      } else {
        this.setLoginDisplay();
      }
    });

    this.msalBroadcastService.inProgress$
    .pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      takeUntil(this._destroying$)
    )
    .subscribe(() => {
      this.setLoginDisplay();
    })
  }

  // convenience getter for easy access to form fields
  get f(): FormGroup["controls"] {
    return this.loginForm.controls;
  }

  onSubmit(): void {
    this.submitted = true;

    // stop here if form is invalid
    if (this.loginForm.invalid) {
      return;
    }
    this.commonService.setUserName(this.f.username.value);

    this.fetchData = true;
    this.authenticationSubscription$ = this.authenticationService
      .login(this.f.username.value, this.f.password.value)
      .pipe(first())
      .subscribe({
        next: (data) => {
          this.commonService.changeData(data['HOME'].some(element => element === 'MANAGE_USERS_1'));
          this.router.navigate(['/']).then(() => this.fetchData=false);
        },
        error: (error) => {
          this.notificationSvc.error(error, 10000);
          this.fetchData = false;
        }
      });
  }

  onSsoLogin() {
    this.setSessionStorageAndLoader("true");
    try {
      this.authenticationService.loginSso();
    } catch (error) {
      this.setSessionStorageAndLoader("false");
    }
  }

  async setLoginDisplay() {
    this.loginStatus = this.msalService.instance.getAllAccounts().length > 0;

    if(this.loginStatus){
      const profile_data = this.msalService.instance.getAllAccounts()[0];
      this.msalService.instance.setActiveAccount(profile_data);

      try {
        if (!this.authenticationService.isMsalLoggedIn()) {
          this.setMsalToken();
        }
        else {
          this.loginUsingSso();
        }
      } catch (error) {
        this.setSessionStorageAndLoader("false");
      }
    } else {
      if(this.authenticationService.isMsalLoggedIn()) {
        await this.onSsoLogin();

        this.setSessionStorageAndLoader("false");
      }
      this.router.navigate(['/login']);
    }
  }

  setMsalToken() {
    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length === 0) {
      this.setSessionStorageAndLoader("false");
      return;
    }
    const tokenRequest = {
      scopes: ["user.read"],
      account: accounts[0]
    };
    this.msalService.acquireTokenSilent(tokenRequest).subscribe({
      next: (tokenResponse) => {
        if (!tokenResponse?.accessToken || !tokenResponse?.idToken) {
          this.setSessionStorageAndLoader("false");
          return;
        }
        const expiry = new Date((new Date()).getTime() + 3600 * 1000);
        // FIX: Moved MSAL tokens from cookies to sessionStorage to prevent RequestHeaderSectionTooLarge error
        sessionStorage.setItem(
          'msal-access-token',
          JSON.stringify({ token: tokenResponse.accessToken, expiry })
        );
        sessionStorage.setItem(
          'msal-id-token',
          JSON.stringify({ token: tokenResponse.idToken, expiry })
        );
        this.loginUsingSso();
      },
      error: () => {
        this.setSessionStorageAndLoader("false");
      }
    });
  }

  loginUsingSso() {
    const msalAccessToken = sessionStorage.getItem('msal-access-token');
    const msalIdToken = sessionStorage.getItem('msal-id-token');
    if (!msalAccessToken || !msalIdToken) {
      this.setSessionStorageAndLoader("false");
      return;
    }
    try {
      const accessTokenData = JSON.parse(msalAccessToken);
      const idTokenData = JSON.parse(msalIdToken);
      if (!accessTokenData?.token || !idTokenData?.token) {
        this.setSessionStorageAndLoader("false");
        return;
      }
      const activeAccount = this.msalService.instance.getActiveAccount();
      if (!activeAccount?.username) {
        this.setSessionStorageAndLoader("false");
        return;
      }
      this.authenticationService.loginUsingSso({
        accessToken: accessTokenData.token,
        idToken: idTokenData.token,
        email: activeAccount.username
      }).subscribe({
        next: () => {
          this.setSessionStorageAndLoader("false");
          this.router.navigate(['/']);
        },
        error: () => {
          this.setSessionStorageAndLoader("false");
        }
      });
    } catch {
      this.setSessionStorageAndLoader("false");
    }
  }

  private setSessionStorageAndLoader(value: string) {
    this.loginLoader = value;
    sessionStorage.setItem('isAuthLoading', value);
  }

  private getDomainFromURL(url: string) {
    const parts = url.split('.');
    if(parts.length > 2) 
      return parts.slice(-2).join('.');
    else
      return parts[0];
  }

  ngOnDestroy(): void {
    this.authenticationSubscription$?.unsubscribe();
    this._destroying$.next(null);
    this._destroying$.complete();
  }
}
